import React, { useState, useEffect, useMemo } from 'react';
import { FaUniversity, FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

const StatCard = ({ title, value, icon, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm">{title}</p>
        <h3 className="text-3xl font-bold">{value}</h3>
      </div>
      <div className="text-4xl opacity-80">{icon}</div>
    </div>
  </div>
);

export default function BancosTable() {
  const [bancos, setBancos] = useState([]);
  const [filteredBancos, setFilteredBancos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('nombreBanco');
  const [sortDirection, setSortDirection] = useState('asc');
  const [bankNameFilter, setBankNameFilter] = useState('');

  useEffect(() => {
    fetchBancos();
  }, []);

  useEffect(() => {
    filterAndSortBancos();
  }, [searchTerm, bancos, sortColumn, sortDirection, bankNameFilter]);

  const fetchBancos = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/bancos');
      setBancos(response.data);
      setFilteredBancos(response.data);
    } catch (err) {
      setError('Error al cargar los bancos. Por favor, intente de nuevo.');
      console.error('Error fetching bancos:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortBancos = () => {
    let filtered = bancos.filter(banco =>
      (banco.nombreBanco.toLowerCase().includes(searchTerm.toLowerCase()) ||
      banco.iban.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (bankNameFilter === '' || banco.nombreBanco === bankNameFilter)
    );

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredBancos(sorted);
    setCurrentPage(1);
  };

  const handleDeleteBanco = async (codigo) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este banco?')) {
      setIsLoading(true);
      setError(null);
      try {
        await axios.delete(`/bancos/${codigo}`);
        await fetchBancos();
      } catch (err) {
        setError('Error al eliminar el banco. Por favor, intente de nuevo.');
        console.error('Error deleting banco:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBancos.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const uniqueBankNames = useMemo(() => {
    return ['', ...new Set(bancos.map(banco => banco.nombreBanco))];
  }, [bancos]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center mt-8 text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">Gestión de Bancos</h1>
      <div className="flex justify-center mb-8">
        <StatCard title="Total Bancos" value={bancos.length} icon={<FaUniversity />} color="from-blue-500 to-blue-700" />
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Bancos</h2>
          <Link
            to="/home/bancos/add"
            className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-900 transition-colors duration-200 flex items-center"
          >
            <FaPlus className="mr-2" /> Añadir Banco
          </Link>
        </div>
        <div className="p-4 flex flex-wrap justify-between items-center bg-gray-100">
          <div className="flex items-center mb-2 md:mb-0">
            <FaSearch className="text-blue-700 mr-2" />
            <input
              type="text"
              placeholder="Buscar por Nombre o IBAN"
              className="border p-2 rounded w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center space-x-4">
            <div>
              <label htmlFor="bankNameFilter" className="mr-2">Filtrar por Banco:</label>
              <select
                id="bankNameFilter"
                className="border p-2 rounded"
                value={bankNameFilter}
                onChange={(e) => setBankNameFilter(e.target.value)}
              >
                {uniqueBankNames.map((name, index) => (
                  <option key={index} value={name}>{name || 'Todos'}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="itemsPerPage" className="mr-2">Mostrar:</label>
              <select
                id="itemsPerPage"
                className="border p-2 rounded"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={filteredBancos.length}>Todos</option>
              </select>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('codigo')}>
                  Código {sortColumn === 'codigo' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('nombreBanco')}>
                  Nombre del Banco {sortColumn === 'nombreBanco' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('iban')}>
                  IBAN {sortColumn === 'iban' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((banco) => (
                <tr key={banco.codigo} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">{banco.codigo}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{banco.nombreBanco}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{banco.iban}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <Link to={`/home/bancos/${banco.codigo}`} className="text-blue-600 hover:text-blue-900">
                        <FaEye className="h-5 w-5" />
                      </Link>
                      <Link to={`/home/bancos/edit/${banco.codigo}`} className="text-green-600 hover:text-green-900">
                        <FaEdit className="h-5 w-5" />
                      </Link>
                      <button onClick={() => handleDeleteBanco(banco.codigo)} className="text-red-600 hover:text-red-900">
                        <FaTrash className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 flex justify-between items-center bg-gray-50">
          <div>
            Mostrando {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredBancos.length)} de {filteredBancos.length} bancos
          </div>
          <div>
            {Array.from({ length: Math.ceil(filteredBancos.length / itemsPerPage) }, (_, i) => (
              <button
                key={i}
                onClick={() => paginate(i + 1)}
                className={`mx-1 px-3 py-1 rounded ${currentPage === i + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}